import React, { useState, useEffect } from 'react';
import { useNotifier } from '../ui/Notifier';
import { calculoService, CalculoData, getClienteNome, getClienteEmail, getClienteTelefone, getClienteCpfCnpj, getClienteTipoPessoa, getProdutoNome } from '../../services/calculoService';
import { formatCpfCnpj as formatCpfCnpjUtil } from '../../utils/formatters';

interface EditCalculoModalProps {
  calculo: CalculoData | null | undefined;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

// Parser de moeda
const parseCurrency = (value: string): number => {
  if (!value) return 0;
  const cleanValue = value.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
  return parseFloat(cleanValue) || 0;
};

// Formatar moeda para exibição
const formatCurrency = (value: number | string | undefined): string => {
  if (!value) return '';
  const num = typeof value === 'string' ? parseCurrency(value) : value;
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Formatar percentual
const formatPercentual = (value: number | string | undefined): string => {
  if (!value) return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Formatar data para input type="date"
const toInputDate = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    const dt = new Date(value);
    if (!Number.isNaN(dt.getTime())) return dt.toISOString().slice(0, 10);
  }
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return '';
};

export const EditCalculoModal: React.FC<EditCalculoModalProps> = ({
  calculo,
  isOpen,
  onClose,
  onSave,
}) => {
  const notifier = useNotifier();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    nomeCliente: '',
    cpfCnpj: '',
    email: '',
    celular: '',
    dddCelular: '',
    tipoPessoa: '',
    produto: '',
    item: '',
    seguradora: '',
    valor: '',
    percentualComis: '',
    valorComis: '',
    inicioVigencia: '',
    zerokm: false,
    perdido: false,
    aprovado: false,
    informacoes: '',
  });

  // Preencher o formulário quando o cálculo é carregado
  useEffect(() => {
    if (calculo) {
      // Extrair telefone do formato "(XX) XXXXX-XXXX" se necessário
      const telefoneCompleto = getClienteTelefone(calculo);
      let ddd = calculo.dddCelular || '';
      let cel = calculo.celular || '';
      
      if (telefoneCompleto && telefoneCompleto !== '-' && !ddd && !cel) {
        // Tentar extrair DDD e telefone do formato completo
        const match = telefoneCompleto.match(/\((\d{2})\)\s*(.+)/);
        if (match) {
          ddd = match[1];
          cel = match[2].replace(/\D/g, '');
        }
      }

      // Formatar CPF/CNPJ ao carregar
      const cpfCnpjValue = getClienteCpfCnpj(calculo);
      const cpfCnpjFormatted = formatCpfCnpjUtil(cpfCnpjValue);

      setFormData({
        nomeCliente: getClienteNome(calculo),
        cpfCnpj: cpfCnpjFormatted === '-' ? cpfCnpjValue : cpfCnpjFormatted,
        email: getClienteEmail(calculo),
        celular: cel,
        dddCelular: ddd,
        tipoPessoa: getClienteTipoPessoa(calculo),
        produto: getProdutoNome(calculo),
        item: calculo.item || '',
        seguradora: calculo.seguradora || '',
        valor: formatCurrency(calculo.valor),
        percentualComis: formatPercentual(calculo.percentualComis),
        valorComis: formatCurrency(calculo.valorComis),
        inicioVigencia: toInputDate(calculo.inicioVigencia),
        zerokm: calculo.zerokm || false,
        perdido: calculo.perdido || false,
        aprovado: calculo.aprovado || false,
        informacoes: calculo.informacoes || '',
      });
    }
  }, [calculo]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/\D/g, '');
    const formattedValue = formatCurrency(parseFloat(numericValue) / 100);
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleCpfCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    // Remove todos os caracteres não numéricos
    const numbers = value.replace(/\D/g, '');
    // Limita a 14 dígitos (CNPJ)
    const limited = numbers.slice(0, 14);
    // Formata com a máscara
    const formatted = formatCpfCnpjUtil(limited);
    setFormData(prev => ({ ...prev, cpfCnpj: formatted === '-' ? limited : formatted }));
  };

  const handlePercentualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/\D/g, '');
    const formattedValue = formatPercentual(parseFloat(numericValue) / 100);
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!calculo?.id) {
      notifier.show({ message: 'ID do cálculo não encontrado', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      const updateData: Partial<CalculoData> = {
        nomeCliente: formData.nomeCliente || undefined,
        cpfCnpj: formData.cpfCnpj || undefined,
        email: formData.email || undefined,
        celular: formData.celular || undefined,
        dddCelular: formData.dddCelular || undefined,
        tipoPessoa: formData.tipoPessoa || undefined,
        produto: formData.produto || undefined,
        item: formData.item || undefined,
        seguradora: formData.seguradora || undefined,
        valor: formData.valor ? parseCurrency(formData.valor) : undefined,
        percentualComis: formData.percentualComis ? parseCurrency(formData.percentualComis) : undefined,
        valorComis: formData.valorComis ? parseCurrency(formData.valorComis) : undefined,
        inicioVigencia: formData.inicioVigencia || undefined,
        zerokm: formData.zerokm,
        perdido: formData.perdido,
        aprovado: formData.aprovado,
        informacoes: formData.informacoes || undefined,
      };

      const response = await calculoService.update(calculo.id, updateData);

      if (response.success) {
        notifier.show({ message: 'Cálculo atualizado com sucesso!', severity: 'success' });
        onSave();
        onClose();
      } else {
        notifier.show({ message: response.message || 'Erro ao atualizar cálculo', severity: 'error' });
      }
    } catch (error: any) {
      notifier.show({ 
        message: error.message || 'Erro ao atualizar cálculo', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 backdrop-blur-sm" style={{ backgroundColor: 'rgba(61, 43, 86, 0.5)' }}>
      <div className="bg-white w-[95vw] max-w-[1000px] max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden">
        {/* Header com dados principais */}
        <div className="bg-white px-4 py-3" style={{ borderBottom: '2px solid #C3B4DE' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: '#F4EFFA' }}>
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">Editar Cálculo</h3>
                <div className="flex items-center space-x-4 text-xs text-gray-500 mt-0.5">
                  {formData.nomeCliente && (
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {formData.nomeCliente}
                    </span>
                  )}
                  {formData.produto && (
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      {formData.produto}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              {/* Nome do Cliente */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                  Nome do Cliente
                </label>
                <input
                  type="text"
                  name="nomeCliente"
                  value={formData.nomeCliente}
                  onChange={handleInputChange}
                  className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                  style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                />
              </div>

              {/* CPF/CNPJ */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                  CPF/CNPJ
                </label>
                <input
                  type="text"
                  name="cpfCnpj"
                  value={formData.cpfCnpj}
                  onChange={handleCpfCnpjChange}
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  maxLength={18}
                  className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                  style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                />
              </div>

              {/* E-mail */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                  E-mail
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                  style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                />
              </div>

              {/* DDD Celular */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                    DDD
                  </label>
                  <input
                    type="text"
                    name="dddCelular"
                    value={formData.dddCelular}
                    onChange={handleInputChange}
                    maxLength={2}
                    className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                    style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                    Celular
                  </label>
                  <input
                    type="text"
                    name="celular"
                    value={formData.celular}
                    onChange={handleInputChange}
                    className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                    style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                  />
                </div>
              </div>

              {/* Tipo Pessoa */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                  Tipo Pessoa
                </label>
                <select
                  name="tipoPessoa"
                  value={formData.tipoPessoa}
                  onChange={handleInputChange}
                  className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                  style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                >
                  <option value="">Selecione</option>
                  <option value="Física">Física</option>
                  <option value="Jurídica">Jurídica</option>
                </select>
              </div>

              {/* Produto */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                  Produto
                </label>
                <input
                  type="text"
                  name="produto"
                  value={formData.produto}
                  onChange={handleInputChange}
                  className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                  style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                />
              </div>

              {/* Item */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                  Item
                </label>
                <input
                  type="text"
                  name="item"
                  value={formData.item}
                  onChange={handleInputChange}
                  className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                  style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                />
              </div>

              {/* Seguradora */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                  Seguradora
                </label>
                <input
                  type="text"
                  name="seguradora"
                  value={formData.seguradora}
                  onChange={handleInputChange}
                  className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                  style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                />
              </div>

              {/* Valor */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                  Valor (R$)
                </label>
                <input
                  type="text"
                  name="valor"
                  value={formData.valor}
                  onChange={handleCurrencyChange}
                  placeholder="0,00"
                  className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                  style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                />
              </div>

              {/* Percentual Comissão */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                  Percentual Comissão (%)
                </label>
                <input
                  type="text"
                  name="percentualComis"
                  value={formData.percentualComis}
                  onChange={handlePercentualChange}
                  placeholder="0,00"
                  className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                  style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                />
              </div>

              {/* Valor Comissão */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                  Valor Comissão (R$)
                </label>
                <input
                  type="text"
                  name="valorComis"
                  value={formData.valorComis}
                  onChange={handleCurrencyChange}
                  placeholder="0,00"
                  className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                  style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                />
              </div>

              {/* Início Vigência */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                  Início Vigência
                </label>
                <input
                  type="date"
                  name="inicioVigencia"
                  value={formData.inicioVigencia}
                  onChange={handleInputChange}
                  className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                  style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                />
              </div>

              {/* Checkboxes */}
              <div className="flex items-center gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="zerokm"
                    checked={formData.zerokm}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded focus:ring-1"
                    style={{ color: '#7A61B2' }}
                  />
                  <label className="ml-2 text-xs font-medium" style={{ color: '#3d2b56' }}>
                    0 KM
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="aprovado"
                    checked={formData.aprovado}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded focus:ring-1"
                    style={{ color: '#7A61B2' }}
                  />
                  <label className="ml-2 text-xs font-medium" style={{ color: '#3d2b56' }}>
                    Aprovado
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="perdido"
                    checked={formData.perdido}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded focus:ring-1"
                    style={{ color: '#7A61B2' }}
                  />
                  <label className="ml-2 text-xs font-medium" style={{ color: '#3d2b56' }}>
                    Perdido
                  </label>
                </div>
              </div>

              {/* Informações - ocupa 2 colunas */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                  Informações Adicionais
                </label>
                <textarea
                  name="informacoes"
                  value={formData.informacoes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs resize-none"
                  style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                  placeholder="Digite informações adicionais sobre o cálculo..."
                />
              </div>
            </div>
          </div>

          {/* Footer com botões */}
          <div className="px-6 py-3 flex justify-end gap-2" style={{ borderTop: '2px solid #C3B4DE', backgroundColor: '#F9F8FC' }}>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 text-xs font-medium rounded-md transition-colors"
              style={{ backgroundColor: '#E5E5E5', color: '#3d2b56' }}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-1.5 text-xs font-medium text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#7A61B2' }}
              disabled={loading}
            >
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
